// Collect the leaf nodes (rows with no children) of a tree. These are the most
// granular rows and the only ones where a real metric pair (e.g. Count +
// SumDuration) exists; parent rows hold aggregates.
export const collectLeafNodes = (nodes, acc = []) => {
  if (!nodes) return acc;
  nodes.forEach((node) => {
    if (node.children && node.children.length > 0) {
      collectLeafNodes(node.children, acc);
    } else {
      acc.push(node);
    }
  });
  return acc;
};

// Distinct dimension values per column across the whole tree. Captured from the
// unfiltered load so filter dropdowns keep listing every option even after a
// server-side filter shrinks the returned data.
export const collectColumnValues = (nodes) => {
  const sets = {};
  const scan = (list) => {
    (list || []).forEach((node) => {
      if (node.dimensions) {
        Object.keys(node.dimensions).forEach((col) => {
          const val = node.dimensions[col];
          if (val !== undefined && val !== null) {
            if (!sets[col]) sets[col] = new Set();
            sets[col].add(val);
          }
        });
      }
      if (node.children && node.children.length) scan(node.children);
    });
  };
  scan(nodes);
  const result = {};
  Object.keys(sets).forEach((col) => { result[col] = Array.from(sets[col]).sort(); });
  return result;
};

// Prune a tree down to the leaf nodes that satisfy `predicate`, keeping the
// ancestor branches that lead to them. Used for the criteria "drill-in" so the
// table can show only the rows behind a clicked criterion count.
export const filterTreeByLeafPredicate = (nodes, predicate) => {
  if (!nodes) return [];
  return nodes.reduce((acc, node) => {
    const isLeaf = !node.children || node.children.length === 0;
    if (isLeaf) {
      if (predicate(node)) acc.push(node);
    } else {
      const children = filterTreeByLeafPredicate(node.children, predicate);
      if (children.length > 0) acc.push({ ...node, children });
    }
    return acc;
  }, []);
};

// Which of `keys` exist on a dimension/metric of any node in the tree.
export const collectPresentKeys = (nodes, keys) => {
  const found = new Set();
  const scan = (list) => {
    (list || []).forEach(node => {
      keys.forEach(k => {
        if ((node.dimensions && node.dimensions[k] !== undefined) ||
            (node.metrics && node.metrics[k] !== undefined)) {
          found.add(k);
        }
      });
      if (node.children && node.children.length) scan(node.children);
    });
  };
  scan(nodes);
  return found;
};

const filterNodesRecursive = (nodes, filters, metricKeys) => {
  const filterKeys = Object.keys(filters);
  return nodes.reduce((acc, node) => {
    // 1. Check if this node matches the filters it possesses
    const isLeaf = !node.children || node.children.length === 0;
    const possessedKeys = filterKeys.filter(k =>
      metricKeys.has(k)
      ? (isLeaf && node.metrics && node.metrics[k] !== undefined)
      : ((node.dimensions && node.dimensions[k] !== undefined) ||
         (node.metrics && node.metrics[k] !== undefined))
    );

    const satisfiesPossessed = possessedKeys.every(k => {
      const val = node.dimensions[k] !== undefined ? node.dimensions[k] : node.metrics[k];
      const selected = filters[k];
      const stringVal = String(val);
      return Array.isArray(selected)
        ? selected.map(String).includes(stringVal)
        : stringVal === String(selected);
    });

    // Prune if it explicitly mismatches a key it has
    if (possessedKeys.length > 0 && !satisfiesPossessed) {
      return acc;
    }

    // 2. Recurse to children
    const filteredChildren = filterNodesRecursive(node.children || [], filters, metricKeys);

    // 3. Decision
    const isExplicitMatch = possessedKeys.length > 0 && satisfiesPossessed;
    const hasMatchingChildren = filteredChildren.length > 0;

    // Keep if branch leads to a match or is a match itself
    if (isExplicitMatch || hasMatchingChildren) {
      acc.push({
        ...node,
        children: filteredChildren
      });
    }

    return acc;
  }, []);
};

export const filterNodes = (nodes, filters, metricKeys = new Set()) => {
  const filterKeys = Object.keys(filters || {});
  if (filterKeys.length === 0) return nodes;

  // Ignore filter keys that don't exist anywhere in the data (e.g. a leftover
  // filter from a dimension that was removed). Without this, such a key would
  // prune every row — nothing can "match" a column that isn't there — and
  // wrongly show "No data available".
  const presentKeys = collectPresentKeys(nodes, filterKeys);
  if (presentKeys.size === 0) return nodes;

  const effectiveFilters = {};
  filterKeys.forEach(k => { if (presentKeys.has(k)) effectiveFilters[k] = filters[k]; });

  return filterNodesRecursive(nodes, effectiveFilters, metricKeys);
};


export const excludeNodes = (nodes, excludes) => {
  const keys = Object.keys(excludes || {});
  if (keys.length === 0) return nodes;

  const isExcluded = (node) =>
    keys.some((k) => {
      const val = (node.dimensions && node.dimensions[k] !== undefined) ? node.dimensions[k]
        : (node.metrics && node.metrics[k] !== undefined) ? node.metrics[k] : undefined;
      return val !== undefined && (excludes[k] || []).map(String).includes(String(val));
    });

  const walk = (list) =>
    (list || []).reduce((acc, node) => {
      if (isExcluded(node)) return acc;
      acc.push(node.children && node.children.length
        ? { ...node, children: walk(node.children) }
        : node);
      return acc;
    }, []);

  return walk(nodes);
};


// Shared comparator for a list of sort rules. Reads each rule's key from the
// node's dimensions or metrics map and applies the rule direction.
const compareByRules = (a, b, sortRules) => {
  for (const rule of sortRules) {
    let aVal, bVal;
    if (a.dimensions && a.dimensions[rule.key] !== undefined) {
      aVal = a.dimensions[rule.key];
      bVal = b.dimensions[rule.key];
    } else if (a.metrics && a.metrics[rule.key] !== undefined) {
      aVal = a.metrics[rule.key];
      bVal = b.metrics[rule.key];
    }

    // Handle null/undefined
    if (aVal === null || aVal === undefined) aVal = 0;
    if (bVal === null || bVal === undefined) bVal = 0;

    if (aVal < bVal) return rule.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return rule.direction === 'asc' ? 1 : -1;
  }
  return 0;
};

export const sortNodes = (nodes, sortRules, dimensionsArray = [], metricsArray = []) => {
  if (!nodes || nodes.length === 0) return [];
  if (!sortRules || sortRules.length === 0) return nodes;

  const sorted = [...nodes].sort((a, b) => compareByRules(a, b, sortRules));

  return sorted.map(node => ({
    ...node,
    children: sortNodes(node.children, sortRules, dimensionsArray, metricsArray)
  }));
};

// Flat (non-recursive) sort, used by the flattened leaf view so a column sort
// orders the entire table globally rather than within each group.
export const sortFlatRows = (rows, sortRules) => {
  if (!rows || rows.length === 0) return rows;
  if (!sortRules || sortRules.length === 0) return rows;
  return [...rows].sort((a, b) => compareByRules(a, b, sortRules));
};
